import type { ISortableListModule, DotNetObjectReference } from '../types';

let dragAutoScrollState: {
    scrollableParent: HTMLElement;
    cursorY: number;
    animationId: number;
} | null = null;

// Instance-based mousemove-Event-Handling (keyed by source list ID)
const globalMouseMoveStates = new Map<string, {
    dotNetRef: DotNetObjectReference;
    itemKey: string;
    handler: (e: MouseEvent) => void;
}>();

// Instance-based mouseup-Event-Handling für Cross-List-Drop (keyed by source list ID)
const globalMouseUpStates = new Map<string, {
    dotNetRef: DotNetObjectReference;
    sourceListId: string;
    groupListIds: string[];
    handler: (e: MouseEvent) => void;
}>();

// Instance-based Cross-List-Hover state für Placeholder in Zielliste (keyed by source list ID)
const crossListHoverStates = new Map<string, {
    sourceListDotNetRef: DotNetObjectReference;
    targetListDotNetRefs: Map<string, DotNetObjectReference>;
    currentTargetListId: string | null;
    sourceListId: string;
}>();

function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
        const style = getComputedStyle(parent);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

const SortableListModule: ISortableListModule = {

    startDragAutoScroll(element: HTMLElement): number[] | null {
        if (!element) return null;

        const scrollable = findScrollableAncestor(element);
        if (!scrollable) return null;

        const rect = scrollable.getBoundingClientRect();

        dragAutoScrollState = {
            scrollableParent: scrollable,
            cursorY: 0,
            animationId: 0
        };

        const edgeZone = 40;
        const maxSpeed = 8;

        const scrollLoop = (): void => {
            if (!dragAutoScrollState) return;

            const sp = dragAutoScrollState.scrollableParent;
            const spRect = sp.getBoundingClientRect();
            const cy = dragAutoScrollState.cursorY;

            if (cy > 0) {
                if (cy < spRect.top + edgeZone && sp.scrollTop > 0) {
                    const t = Math.max(0, Math.min(1, 1 - (cy - spRect.top) / edgeZone));
                    sp.scrollTop -= maxSpeed * t;
                } else if (cy > spRect.bottom - edgeZone && sp.scrollTop < sp.scrollHeight - sp.clientHeight) {
                    const t = Math.max(0, Math.min(1, 1 - (spRect.bottom - cy) / edgeZone));
                    sp.scrollTop += maxSpeed * t;
                }
            }

            dragAutoScrollState.animationId = requestAnimationFrame(scrollLoop);
        };

        dragAutoScrollState.animationId = requestAnimationFrame(scrollLoop);

        return [rect.top, rect.bottom];
    },

    updateDragCursorY(cursorY: number): void {
        if (dragAutoScrollState) {
            dragAutoScrollState.cursorY = cursorY;
        }
    },

    stopDragAutoScroll(): void {
        if (dragAutoScrollState) {
            cancelAnimationFrame(dragAutoScrollState.animationId);
            dragAutoScrollState = null;
        }
    },

    scrollItemIntoView(container: HTMLElement, selector: string): void {
        if (!container) return;

        const item = container.querySelector(selector) as HTMLElement;
        if (!item) return;

        const scrollable = findScrollableAncestor(container);
        if (!scrollable) return;

        const scrollableRect = scrollable.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        if (itemRect.top < scrollableRect.top) {
            scrollable.scrollTop -= (scrollableRect.top - itemRect.top);
        } else if (itemRect.bottom > scrollableRect.bottom) {
            scrollable.scrollTop += (itemRect.bottom - scrollableRect.bottom);
        }
    },

    captureItemDimensions(containerId: string, itemIndex: number): number[] {
        const container = document.getElementById(containerId);
        if (!container) return [0, 0, 0];

        const items = container.querySelectorAll('.sortable-list-item:not([data-placeholder])');
        if (items[itemIndex]) {
            const rect = items[itemIndex].getBoundingClientRect();
            return [rect.left, rect.width, rect.height];
        }
        return [0, 0, 0];
    },

    captureContainerBounds(containerId: string): number[] {
        const container = document.getElementById(containerId);
        if (container) {
            const rect = container.getBoundingClientRect();
            return [rect.top, rect.bottom, rect.width];
        }
        return [0, window.innerHeight, 0];
    },

    attachGlobalMouseMove(dotNetRef: DotNetObjectReference, sourceListId: string, itemKey: string): void {
        // Entferne vorhandenen Listener für diese Liste falls vorhanden
        this.detachGlobalMouseMove(sourceListId);

        const handler = (e: MouseEvent): void => {
            const state = globalMouseMoveStates.get(sourceListId);
            if (state) {
                state.dotNetRef.invokeMethodAsync(
                    'OnGlobalMouseMove',
                    e.clientX,
                    e.clientY
                );
            }

            // Cross-List-Hover-Erkennung
            const hoverState = crossListHoverStates.get(sourceListId);
            if (hoverState) {
                const x = e.clientX;
                const y = e.clientY;
                let foundTargetId: string | null = null;
                let foundTargetIndex = -1;

                for (const [listId] of hoverState.targetListDotNetRefs) {
                    const listEl = document.getElementById(listId);
                    if (!listEl) continue;

                    const rect = listEl.getBoundingClientRect();
                    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                        foundTargetId = listId;

                        // Prüfe ob die Liste leer ist (data-empty-drop-zone vorhanden)
                        const emptyDropZone = listEl.querySelector('[data-empty-drop-zone]');
                        if (emptyDropZone) {
                            // Leere Liste - Index = 0
                            foundTargetIndex = 0;
                        } else {
                            // Berechne Zielindex für nicht-leere Listen
                            const items = listEl.querySelectorAll('.sortable-list-item:not([data-placeholder]):not(.external-placeholder)');
                            foundTargetIndex = items.length;

                            for (let i = 0; i < items.length; i++) {
                                const itemRect = items[i].getBoundingClientRect();
                                const midY = itemRect.top + itemRect.height / 2;
                                if (y < midY) {
                                    foundTargetIndex = i;
                                    break;
                                }
                            }
                        }
                        break;
                    }
                }

                const prevTarget = hoverState.currentTargetListId;

                // Wenn wir über die Quell-Liste hovern oder nirgendwo, Placeholder in allen Ziellisten entfernen
                if (foundTargetId === null || foundTargetId === hoverState.sourceListId) {
                    if (prevTarget !== null && prevTarget !== hoverState.sourceListId) {
                        const prevRef = hoverState.targetListDotNetRefs.get(prevTarget);
                        if (prevRef) {
                            prevRef.invokeMethodAsync('HideExternalPlaceholder');
                        }
                    }
                    hoverState.currentTargetListId = null;
                } else {
                    // Über einer Zielliste - Placeholder zeigen
                    if (prevTarget !== foundTargetId && prevTarget !== null) {
                        const prevRef = hoverState.targetListDotNetRefs.get(prevTarget);
                        if (prevRef) {
                            prevRef.invokeMethodAsync('HideExternalPlaceholder');
                        }
                    }

                    const targetRef = hoverState.targetListDotNetRefs.get(foundTargetId);
                    if (targetRef) {
                        targetRef.invokeMethodAsync('ShowExternalPlaceholder', foundTargetIndex);
                    }
                    hoverState.currentTargetListId = foundTargetId;
                }
            }
        };

        globalMouseMoveStates.set(sourceListId, {
            dotNetRef,
            itemKey,
            handler
        });

        document.addEventListener('mousemove', handler);
    },

    detachGlobalMouseMove(sourceListId?: string): void {
        if (sourceListId) {
            const state = globalMouseMoveStates.get(sourceListId);
            if (state) {
                document.removeEventListener('mousemove', state.handler);
                globalMouseMoveStates.delete(sourceListId);
            }
        } else {
            // Fallback: alle entfernen
            for (const [, state] of globalMouseMoveStates) {
                document.removeEventListener('mousemove', state.handler);
            }
            globalMouseMoveStates.clear();
        }
    },

    registerCrossListHover(sourceListDotNetRef: DotNetObjectReference, sourceListId: string, targetListIds: string[], targetListDotNetRefs: DotNetObjectReference[]): void {
        this.unregisterCrossListHover(sourceListId);

        const refMap = new Map<string, DotNetObjectReference>();
        for (let i = 0; i < targetListIds.length; i++) {
            refMap.set(targetListIds[i], targetListDotNetRefs[i]);
        }

        crossListHoverStates.set(sourceListId, {
            sourceListDotNetRef: sourceListDotNetRef,
            targetListDotNetRefs: refMap,
            currentTargetListId: null,
            sourceListId
        });
    },

    unregisterCrossListHover(sourceListId?: string): void {
        if (sourceListId) {
            const state = crossListHoverStates.get(sourceListId);
            if (state) {
                if (state.currentTargetListId) {
                    const ref = state.targetListDotNetRefs.get(state.currentTargetListId);
                    if (ref) {
                        ref.invokeMethodAsync('HideExternalPlaceholder');
                    }
                }
                crossListHoverStates.delete(sourceListId);
            }
        } else {
            // Fallback: alle entfernen
            for (const [, state] of crossListHoverStates) {
                if (state.currentTargetListId) {
                    const ref = state.targetListDotNetRefs.get(state.currentTargetListId);
                    if (ref) {
                        ref.invokeMethodAsync('HideExternalPlaceholder');
                    }
                }
            }
            crossListHoverStates.clear();
        }
    },

    attachGlobalMouseUp(dotNetRef: DotNetObjectReference, sourceListId: string, groupListIds: string[]): void {
        this.detachGlobalMouseUp(sourceListId);

        const handler = (e: MouseEvent): void => {
            const state = globalMouseUpStates.get(sourceListId);
            if (!state) return;

            const x = e.clientX;
            const y = e.clientY;
            let targetListId: string | null = null;
            let targetIndex = -1;

            // Prüfe ob Cursor über einer der Gruppen-Listen ist
            const allListIds = [state.sourceListId, ...state.groupListIds];
            for (const listId of allListIds) {
                const listEl = document.getElementById(listId);
                if (!listEl) continue;

                const rect = listEl.getBoundingClientRect();
                if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                    targetListId = listId;

                    // Berechne den Zielindex basierend auf der Y-Position
                    const items = listEl.querySelectorAll('.sortable-list-item:not([data-placeholder])');
                    targetIndex = items.length; // Default: ans Ende

                    for (let i = 0; i < items.length; i++) {
                        const itemRect = items[i].getBoundingClientRect();
                        const midY = itemRect.top + itemRect.height / 2;
                        if (y < midY) {
                            targetIndex = i;
                            break;
                        }
                    }
                    break;
                }
            }

            state.dotNetRef.invokeMethodAsync(
                'OnGlobalMouseUp',
                x,
                y,
                targetListId,
                targetIndex
            );

            // Cleanup nach mouseup
            globalMouseUpStates.delete(sourceListId);
        };

        globalMouseUpStates.set(sourceListId, {
            dotNetRef,
            sourceListId,
            groupListIds,
            handler
        });

        document.addEventListener('mouseup', handler, { once: true });
    },

    detachGlobalMouseUp(sourceListId?: string): void {
        if (sourceListId) {
            const state = globalMouseUpStates.get(sourceListId);
            if (state) {
                document.removeEventListener('mouseup', state.handler);
                globalMouseUpStates.delete(sourceListId);
            }
        } else {
            // Fallback: alle entfernen
            for (const [, state] of globalMouseUpStates) {
                document.removeEventListener('mouseup', state.handler);
            }
            globalMouseUpStates.clear();
        }
    },
};

export default SortableListModule;
